(function (root, factory) {
  const model = typeof module === 'object' && module.exports
    ? require('./dim-text-model.js')
    : root.DIMTextModel;
  const api = factory(model);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DIMTextControls = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, (model) => {
  'use strict';

  const FIELD_SELECTORS = Object.freeze({
    text: '[data-dim-text-field="text"]',
    fontId: '[data-dim-text-field="fontId"]',
    colour: '[data-dim-text-field="colour"]',
    size: '[data-dim-text-field="size"]',
    rotation: '[data-dim-text-field="rotation"]',
    curve: '[data-dim-text-field="curve"]',
    align: '[data-dim-text-field="align"]',
    x: '[data-dim-text-field="x"]',
    y: '[data-dim-text-field="y"]'
  });
  const NUMERIC_FIELDS = new Set(['size', 'rotation', 'curve', 'x', 'y']);
  const cloneLayers = (layers) => model.validateLayers(layers, { allowLegacyMultilineCurve: true }).map((layer) => ({ ...layer }));

  const createTextControls = (rootElement, {
    initialLayers = [],
    fontManifest = null,
    ensureFont = null,
    onChange = () => {},
    onSelectionChange = () => {},
    now = () => Date.now(),
    coalesceWindowMs = 600,
    historyLimit = 50
  } = {}) => {
    if (!rootElement || typeof rootElement.querySelector !== 'function') throw new Error('Text controls need a DOM root.');
    let layers = cloneLayers(initialLayers);
    let selection = { type: 'image', id: null };
    let lastTextId = null;
    const fontSamples = new Map();
    let history = [];
    let future = [];
    let activeCoalesceKey = null;
    let destroyed = false;
    let identifier = 0;
    let suggestion = null;
    let layoutWarning = null;
    let inkColours = null;
    const pendingInvalid = new Map();
    const listeners = [];
    const fonts = fontManifest?.approval?.status === 'approved'
      && fontManifest.fonts && typeof fontManifest.fonts === 'object' ? fontManifest.fonts : null;

    const elements = {
      panel: rootElement.querySelector('[data-dim-text-panel]'),
      imageTab: rootElement.querySelector('[data-dim-text-tab="image"]'),
      textTab: rootElement.querySelector('[data-dim-text-tab="text"]'),
      list: rootElement.querySelector('[data-dim-text-list]'),
      add: rootElement.querySelector('[data-dim-text-add]'),
      remove: rootElement.querySelector('[data-dim-text-delete]'),
      duplicate: rootElement.querySelector('[data-dim-text-duplicate]'),
      undo: rootElement.querySelector('[data-dim-text-undo]'),
      redo: rootElement.querySelector('[data-dim-text-redo]'),
      count: rootElement.querySelector('[data-dim-text-count]'),
      error: rootElement.querySelector('[data-dim-text-error]'),
      placementGuidance: rootElement.querySelector('[data-dim-text-placement-guidance]'),
      fontChoices: rootElement.querySelector('[data-dim-text-font-choices]'),
      curveHelp: rootElement.querySelector('[data-dim-text-curve-help]'),
      straighten: rootElement.querySelector('[data-dim-text-straighten]'),
      fields: Object.fromEntries(Object.entries(FIELD_SELECTORS).map(([field, selector]) => [
        field, rootElement.querySelector(selector)
      ]))
    };

    const createEmptyState = () => {
      const panel = elements.panel;
      const document = rootElement.ownerDocument;
      if (!panel?.insertBefore || !document?.createElement) return null;
      const empty = document.createElement('div');
      empty.className = 'dim-text-controls__empty';
      empty.dataset.dimTextEmpty = '';
      const heading = document.createElement('h2');
      heading.textContent = 'Make it personal with a few words';
      const description = document.createElement('p');
      description.textContent = 'Add a name, a date or a message. You can change the style after you have typed it.';
      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'dim-text-controls__empty-add';
      action.dataset.dimTextEmptyAdd = '';
      action.textContent = 'Add a name or message';
      const optional = document.createElement('p');
      optional.textContent = 'Or keep the artwork just as it is.';
      const unavailable = document.createElement('p');
      unavailable.textContent = 'Text is unavailable until approved fonts are ready.';
      unavailable.hidden = true;
      empty.append(heading, description, action, optional, unavailable);
      panel.insertBefore(empty, panel.firstChild);
      return { empty, action, optional, unavailable };
    };
    const emptyState = createEmptyState();

    const createSuggestion = () => {
      const panel = elements.panel;
      const document = rootElement.ownerDocument;
      if (!panel?.insertBefore || !document?.createElement) return null;
      const container = document.createElement('div');
      container.className = 'dim-text-controls__suggestion';
      container.dataset.dimTextSuggestion = '';
      const label = document.createElement('p');
      label.textContent = 'Suggested wording — add it';
      const wording = document.createElement('p');
      wording.dataset.dimTextSuggestionText = '';
      const action = document.createElement('button');
      action.type = 'button';
      action.dataset.dimTextSuggestionAdd = '';
      action.textContent = 'Add suggested wording';
      container.append(label, wording, action);
      container.hidden = true;
      panel.insertBefore(container, panel.children[1] || null);
      return { container, wording, action };
    };
    const suggestionState = createSuggestion();

    const listen = (element, event, listener) => {
      if (!element?.addEventListener) return;
      element.addEventListener(event, listener);
      listeners.push(() => element.removeEventListener?.(event, listener));
    };

    const retainedFontLabel = (id) => {
      const readable = String(id || 'Saved font')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return `${readable.charAt(0).toUpperCase()}${readable.slice(1)} (retained)`;
    };

    const populateFontChoices = () => {
      const select = elements.fields.fontId;
      if (!select?.replaceChildren || !rootElement.ownerDocument?.createElement) return;
      const approvedEntries = fonts ? Object.entries(fonts) : [];
      const approvedIds = new Set(approvedEntries.map(([id]) => id));
      const retainedIds = [...new Set(layers.map((layer) => layer.fontId).filter((id) => !approvedIds.has(id)))];
      const options = approvedEntries.map(([id, font]) => {
        const option = rootElement.ownerDocument.createElement('option');
        option.value = id;
        option.textContent = String(font.label || id);
        const sample = fontSamples.get(id);
        if (sample?.ready && option.style) {
          option.style.fontFamily = `${JSON.stringify(font.family)}, sans-serif`;
          option.style.fontWeight = String(font.weight);
        }
        return option;
      });
      for (const id of retainedIds) {
        const option = rootElement.ownerDocument.createElement('option');
        option.value = id;
        option.textContent = retainedFontLabel(id);
        option.disabled = true;
        option.dataset.dimTextRetainedFont = 'true';
        options.push(option);
      }
      select.replaceChildren(...options);
    };

    const populateFontSamples = () => {
      const container = elements.fontChoices;
      if (!container?.replaceChildren || !rootElement.ownerDocument?.createElement) return;
      const buttons = Object.entries(fonts || {}).map(([id, font]) => {
        const button = rootElement.ownerDocument.createElement('button');
        button.type = 'button';
        button.dataset.dimTextFont = id;
        button.dataset.dimTextFontReady = 'false';
        const name = rootElement.ownerDocument.createElement('span');
        name.textContent = String(font.label || id);
        const status = rootElement.ownerDocument.createElement('span');
        status.className = 'dim-text-controls__font-status';
        status.textContent = 'Font loading';
        button.append(name, status);
        const sample = { button, name, status, ready: false };
        fontSamples.set(id, sample);
        listen(button, 'click', () => {
          const item = selectedLayer();
          if (!item) return;
          applyPatch(item.id, { fontId: id, fontRevision: font.revision }, { reason: 'text-fontId' });
        });
        // The maintained loader verifies the manifest, bytes, coverage and loaded
        // FontFace. A CSS fallback alone never marks a sample or artwork ready.
        Promise.resolve().then(() => {
          if (typeof ensureFont !== 'function') throw new Error('Font loading is unavailable.');
          return ensureFont(id, font.revision, { text: name.textContent });
        }).then(() => {
          if (destroyed) return;
          sample.ready = true;
          button.dataset.dimTextFontReady = 'true';
          name.style ||= {};
          name.style.fontFamily = `${JSON.stringify(font.family)}, sans-serif`;
          name.style.fontWeight = String(font.weight);
          status.textContent = '';
          status.hidden = true;
          populateFontChoices();
        }).catch(() => {
          if (destroyed) return;
          status.textContent = 'Font preview unavailable';
        });
        return button;
      });
      container.replaceChildren(...buttons);
    };

    const selectedIndex = () => selection.type === 'text'
      ? layers.findIndex((layer) => layer.id === selection.id)
      : -1;
    const selectedLayer = () => layers[selectedIndex()] || null;
    const fontsReady = () => Boolean(fonts && Object.keys(fonts).length);
    const suggestionAvailable = () => Boolean(suggestionState && suggestion && !suggestion.consumed
      && !destroyed && fontsReady() && layers.length < model.MAX_LAYERS && pendingInvalid.size === 0
      && layers.every((layer) => fonts[layer.fontId]?.revision === layer.fontRevision));

    const setError = (error = null) => {
      if (!elements.error) return;
      elements.error.textContent = error ? String(error.message || error) : '';
      elements.error.hidden = !error;
      if (error) elements.error.setAttribute?.('role', 'alert');
      else elements.error.removeAttribute?.('role');
    };

    const firstPending = () => pendingInvalid.values().next().value || null;

    const updateFields = () => {
      const layer = selectedLayer();
      for (const [field, element] of Object.entries(elements.fields)) {
        if (!element) continue;
        const pending = layer ? pendingInvalid.get(layer.id) : null;
        if (pending?.field === field) element.value = pending.value;
        else if (layer) element.value = String(layer[field]);
        element.disabled = !layer || (field === 'fontId' && !fonts) || (field === 'colour' && inkColours !== null)
          || (field === 'curve' && model.hasExplicitLineBreak(layer.text));
        if (pending?.field === field) {
          element.setAttribute?.('aria-invalid', 'true');
          if (elements.error?.id) element.setAttribute?.('aria-errormessage', elements.error.id);
        } else {
          element.removeAttribute?.('aria-invalid');
          element.removeAttribute?.('aria-errormessage');
        }
      }
      if (elements.remove) elements.remove.disabled = !layer;
      if (elements.duplicate) elements.duplicate.disabled = !layer || layers.length >= model.MAX_LAYERS;
      if (elements.straighten) elements.straighten.disabled = !layer || layer.curve === 0;
      if (elements.curveHelp) {
        const multiline = Boolean(layer && model.hasExplicitLineBreak(layer.text));
        elements.curveHelp.textContent = multiline ? 'Curved text works with one line.' : '';
        elements.curveHelp.hidden = !multiline;
      }
      for (const [id, sample] of fontSamples) {
        sample.button.disabled = !layer;
        sample.button.setAttribute?.('aria-pressed', layer?.fontId === id ? 'true' : 'false');
      }
    };

    const layerLabel = (layer, index) => layer.text.trim().split('\n')[0].slice(0, 40) || `Text block ${index + 1}`;

    const updateList = () => {
      if (!elements.list?.replaceChildren || !rootElement.ownerDocument?.createElement) return;
      const buttons = layers.map((layer, index) => {
        const button = rootElement.ownerDocument.createElement('button');
        button.type = 'button';
        button.dataset.dimTextLayerId = layer.id;
        button.textContent = layerLabel(layer, index);
        button.setAttribute?.('aria-pressed', selection.type === 'text' && selection.id === layer.id ? 'true' : 'false');
        return button;
      });
      elements.list.replaceChildren(...buttons);
    };

    const refresh = () => {
      const textSelected = selection.type === 'text';
      elements.imageTab?.removeAttribute?.('aria-selected');
      elements.textTab?.removeAttribute?.('aria-selected');
      elements.imageTab?.setAttribute?.('aria-pressed', textSelected ? 'false' : 'true');
      elements.textTab?.setAttribute?.('aria-pressed', textSelected ? 'true' : 'false');
      if (elements.add) elements.add.disabled = !fonts || !Object.keys(fonts).length || layers.length >= model.MAX_LAYERS;
      if (elements.add) elements.add.textContent = 'Add text';
      if (emptyState) {
        const empty = layers.length === 0 && pendingInvalid.size === 0;
        const canAdd = fontsReady();
        emptyState.empty.hidden = !empty;
        emptyState.action.disabled = !canAdd;
        emptyState.optional.hidden = !canAdd;
        emptyState.unavailable.hidden = canAdd;
        for (const child of elements.panel.children) {
          if (child === emptyState.empty || child === suggestionState?.container || child === elements.error) continue;
          const historyControls = child.contains?.(elements.undo);
          const shouldHide = empty && (!historyControls || (history.length === 0 && future.length === 0));
          if (shouldHide && !child.hidden) {
            child.hidden = true;
            child.dataset.dimTextHiddenByEmpty = '';
          } else if (!shouldHide && child.dataset.dimTextHiddenByEmpty !== undefined) {
            child.hidden = false;
            delete child.dataset.dimTextHiddenByEmpty;
          }
        }
      }
      if (suggestionState) {
        suggestionState.container.hidden = !suggestionAvailable();
        suggestionState.action.disabled = !suggestionAvailable();
      }
      if (elements.count) elements.count.textContent = `${layers.length} of ${model.MAX_LAYERS}`;
      if (elements.undo) elements.undo.disabled = history.length === 0;
      if (elements.redo) elements.redo.disabled = future.length === 0;
      const colour = selectedLayer()?.colour || '';
      for (const swatch of rootElement.querySelectorAll?.('[data-dim-text-colour]') || []) {
        swatch.setAttribute?.('aria-pressed', swatch.dataset?.dimTextColour?.toUpperCase() === colour ? 'true' : 'false');
        const unsupported = inkColours !== null && !inkColours.includes(swatch.dataset?.dimTextColour?.toUpperCase());
        if (unsupported && !swatch.hidden) {
          swatch.hidden = true; swatch.dataset.dimTextHiddenByInk = '';
        } else if (!unsupported && swatch.dataset.dimTextHiddenByInk !== undefined) {
          swatch.hidden = false; delete swatch.dataset.dimTextHiddenByInk;
        }
        swatch.disabled = unsupported || !selectedLayer();
      }
      updateList();
      updateFields();
      const unsupportedInk = inkColours !== null && layers.some(layer => layer.text.trim() && !inkColours.includes(layer.colour));
      const legacyCurve = layers.some(layer => layer.curve && model.hasExplicitLineBreak(layer.text));
      setError(firstPending()?.error || (legacyCurve ? model.legacyCurveError() : null) || layoutWarning || (unsupportedInk ? new Error('Use an admitted ink colour for this Words-only composition.') : null));
    };

    const immutableLayers = () => Object.freeze(cloneLayers(layers).map((layer) => Object.freeze(layer)));
    const emitChange = (reason, details = {}) => onChange(Object.freeze({
      reason,
      layers: immutableLayers(),
      pendingInvalid: pendingInvalid.size > 0,
      ...details
    }));
    const emitSelection = () => onSelectionChange(Object.freeze({ ...selection }));

    const commit = (nextLayers, reason, coalesceKey = null) => {
      const validated = cloneLayers(nextLayers);
      const before = cloneLayers(layers);
      const after = cloneLayers(validated);
      if (JSON.stringify(before) === JSON.stringify(after)) return false;
      const timestamp = Number(now());
      const previous = history[history.length - 1];
      const mayCoalesce = coalesceKey && previous?.key === coalesceKey
        && (activeCoalesceKey === coalesceKey || timestamp - previous.at <= coalesceWindowMs);
      if (mayCoalesce) {
        previous.after = after;
        previous.at = timestamp;
      } else {
        history.push({ before, after, key: coalesceKey, at: timestamp });
        if (history.length > historyLimit) history.shift();
      }
      future = [];
      layers = validated;
      emitChange(reason);
      refresh();
      return true;
    };

    const setPending = (id, field, value, error) => {
      pendingInvalid.set(id, { field, value, error });
      emitChange('text-invalid', Object.freeze({ layerId: id, field, error }));
      refresh();
      const element = elements.fields[field];
      const disclosure = element?.closest?.('details');
      if (disclosure) disclosure.open = true;
      element?.focus?.();
      return false;
    };

    const clearPending = (id) => pendingInvalid.delete(id);

    const applyPatch = (id, patch, { reason = 'text-edit', coalesceKey = null, rawField = null, rawValue = null } = {}) => {
      const index = layers.findIndex((layer) => layer.id === id);
      if (index < 0) throw new Error('Unknown text block.');
      const next = cloneLayers(layers);
      next[index] = { ...next[index], ...patch };
      if (Object.hasOwn(patch, 'text') && typeof patch.text === 'string') {
        next[index].text = model.normaliseLineBreaks(patch.text);
        if (model.hasExplicitLineBreak(next[index].text)) next[index].curve = 0;
      }
      try {
        if (inkColours !== null && Object.hasOwn(patch, 'colour') && !inkColours.includes(String(patch.colour).toUpperCase())) {
          throw Object.assign(new Error('Use an admitted ink colour for this Words-only composition.'), { code: 'WORDS_INK_COLOUR' });
        }
        if (Object.hasOwn(patch, 'curve') && patch.curve && model.hasExplicitLineBreak(next[index].text)) throw model.legacyCurveError();
        model.validateLayers(next, { allowLegacyMultilineCurve: true });
      } catch (error) {
        return setPending(id, rawField || Object.keys(patch)[0], rawValue ?? String(Object.values(patch)[0]), error);
      }
      const correctedPending = pendingInvalid.has(id);
      clearPending(id);
      const changed = commit(next, reason, coalesceKey);
      if (!changed) {
        if (correctedPending) emitChange(reason);
        refresh();
      }
      return changed;
    };

    const selectImage = () => {
      if (selection.type === 'image') return false;
      selection = { type: 'image', id: null };
      refresh();
      emitSelection();
      return true;
    };

    const selectText = (id = null) => {
      const selectedId = id || (layers.some(layer => layer.id === lastTextId) ? lastTextId : null) || layers[0]?.id || null;
      if (selectedId && !layers.some((layer) => layer.id === selectedId)) return false;
      if (selection.type === 'text' && selection.id === selectedId) return false;
      selection = { type: 'text', id: selectedId };
      lastTextId = selectedId;
      refresh();
      emitSelection();
      return true;
    };

    const nextId = () => {
      do { identifier += 1; } while (layers.some((layer) => layer.id === `text-${identifier}`));
      return `text-${identifier}`;
    };

    const makeLayer = (id, text, fontId, font) => ({
      id, text, fontId, fontRevision: font.revision,
      x: 0.5, y: 0.5, size: 0.08, rotation: 0, curve: 0, colour: inkColours?.[0] || '#26334A', align: 'center'
    });

    const addText = (text) => {
      if (!fontsReady()) {
        setError(new Error('No approved text fonts are available.'));
        return false;
      }
      if (layers.length >= model.MAX_LAYERS) {
        setError(new Error('Use no more than three text blocks.'));
        return false;
      }
      const [fontId, font] = Object.entries(fonts)[0];
      const item = makeLayer(nextId(), text, fontId, font);
      try { model.validateLayers([...layers, item], { allowLegacyMultilineCurve: true }); } catch { return false; }
      const changed = commit([...layers, item], 'text-add');
      if (changed) {
        selectText(item.id);
        elements.fields.text?.focus?.();
      }
      return changed;
    };
    const add = () => addText('');

    const setSuggestion = (text, options = {}) => {
      suggestion = null;
      if (suggestionState) suggestionState.wording.textContent = '';
      if (destroyed || typeof text !== 'string' || !text.trim() || !fontsReady() || !suggestionState) {
        refresh();
        return false;
      }
      const [fontId, font] = Object.entries(fonts)[0];
      try { model.validateLayers([makeLayer('suggestion-preview', text, fontId, font)]); }
      catch { refresh(); return false; }
      const isCurrent = options?.isCurrent;
      suggestion = { text, isCurrent: typeof isCurrent === 'function' ? isCurrent : null, consumed: false };
      suggestionState.wording.textContent = text;
      refresh();
      return true;
    };

    const addSuggestion = () => {
      const current = suggestion;
      if (!suggestionAvailable()) return false;
      try {
        if (current.isCurrent && !current.isCurrent()) {
          suggestion = null;
          suggestionState.wording.textContent = '';
          refresh();
          return false;
        }
      } catch {
        suggestion = null;
        suggestionState.wording.textContent = '';
        refresh();
        return false;
      }
      current.consumed = true;
      const added = addText(current.text);
      if (!added && suggestion === current) current.consumed = false;
      if (added && suggestion === current) suggestionState.wording.textContent = '';
      refresh();
      return added;
    };

    const deleteSelected = () => {
      const index = selectedIndex();
      if (index < 0) return false;
      const id = layers[index].id;
      const next = layers.filter((_, layerIndex) => layerIndex !== index);
      pendingInvalid.delete(id);
      const changed = commit(next, 'text-delete');
      if (changed) {
        const replacement = next[Math.min(index, next.length - 1)];
        selection = { type: 'text', id: replacement?.id || null };
        refresh();
        emitSelection();
      }
      return changed;
    };

    const duplicateSelected = () => {
      const item = selectedLayer();
      if (!item) return false;
      if (layers.length >= model.MAX_LAYERS) {
        setError(new Error('Use no more than three text blocks.'));
        return false;
      }
      const duplicate = { ...item, id: nextId(), x: Math.min(2, item.x + 0.02), y: Math.min(2, item.y + 0.02) };
      const changed = commit([...layers, duplicate], 'text-duplicate');
      if (changed) selectText(duplicate.id);
      return changed;
    };

    const undo = () => {
      const command = history.pop();
      if (!command) return false;
      future.push(command);
      layers = cloneLayers(command.before);
      for (const id of [...pendingInvalid.keys()]) if (!layers.some((layer) => layer.id === id)) pendingInvalid.delete(id);
      if (selection.type === 'text' && !layers.some((layer) => layer.id === selection.id)) {
        selection = { type: 'text', id: layers[0]?.id || null };
        emitSelection();
      }
      emitChange('text-undo');
      refresh();
      return true;
    };

    const redo = () => {
      const command = future.pop();
      if (!command) return false;
      history.push(command);
      layers = cloneLayers(command.after);
      if (selection.type === 'text' && !layers.some((layer) => layer.id === selection.id)) {
        selection = { type: 'text', id: layers[0]?.id || null };
        emitSelection();
      }
      emitChange('text-redo');
      refresh();
      return true;
    };

    const setLayers = (nextLayers, { preserveHistory = false, dirty = false, reason = 'text-restore' } = {}) => {
      const validated = cloneLayers(nextLayers);
      layers = validated;
      populateFontChoices();
      pendingInvalid.clear();
      activeCoalesceKey = null;
      if (!preserveHistory) {
        history = [];
        future = [];
      }
      if (selection.type === 'text' && !layers.some((layer) => layer.id === selection.id)) {
        selection = { type: 'text', id: layers[0]?.id || null };
        emitSelection();
      }
      if (dirty) emitChange(reason);
      refresh();
      return immutableLayers();
    };

    const handleField = (field, event) => {
      const item = selectedLayer();
      if (!item) return;
      const raw = event.target.value;
      let value = raw;
      if (NUMERIC_FIELDS.has(field)) value = Number(raw);
      const patch = { [field]: value };
      if (field === 'fontId') {
        const font = fonts?.[value];
        if (!font) return setPending(item.id, field, raw, new Error('The selected font is unavailable.'));
        patch.fontRevision = font.revision;
      }
      applyPatch(item.id, patch, {
        reason: `text-${field}`,
        coalesceKey: `${item.id}:${field}`,
        rawField: field,
        rawValue: raw
      });
    };

    const beginCoalescedEdit = (key) => { activeCoalesceKey = String(key || ''); };
    const endCoalescedEdit = () => { activeCoalesceKey = null; };

    listen(elements.imageTab, 'click', selectImage);
    listen(elements.textTab, 'click', () => selectText());
    listen(elements.add, 'click', add);
    listen(emptyState?.action, 'click', add);
    listen(suggestionState?.action, 'click', addSuggestion);
    listen(elements.remove, 'click', deleteSelected);
    listen(elements.duplicate, 'click', duplicateSelected);
    listen(elements.undo, 'click', undo);
    listen(elements.redo, 'click', redo);
    listen(elements.straighten, 'click', () => {
      const item = selectedLayer();
      if (item) applyPatch(item.id, { curve: 0 }, { reason: 'text-straighten' });
    });
    listen(elements.list, 'click', (event) => {
      const button = event.target?.closest?.('[data-dim-text-layer-id]');
      if (button?.dataset?.dimTextLayerId) selectText(button.dataset.dimTextLayerId);
    });
    for (const [field, element] of Object.entries(elements.fields)) {
      listen(element, 'input', (event) => handleField(field, event));
      listen(element, 'pointerdown', () => beginCoalescedEdit(`${selection.id}:${field}`));
      listen(element, 'pointerup', endCoalescedEdit);
      listen(element, 'change', endCoalescedEdit);
    }

    listen(rootElement, 'keydown', (event) => {
      const item = selectedLayer();
      if (!item || ['TEXTAREA', 'INPUT', 'SELECT'].includes(event.target?.tagName)) return;
      const movement = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
      if (!movement) return;
      event.preventDefault();
      const step = event.shiftKey ? 0.01 : 0.001;
      applyPatch(item.id, { x: item.x + movement[0] * step, y: item.y + movement[1] * step }, {
        reason: 'text-nudge', coalesceKey: `${item.id}:nudge`
      });
    });

    listen(rootElement, 'click', (event) => {
      const item = selectedLayer();
      if (!item) return;
      const target = event.target?.closest?.('[data-dim-text-colour],[data-dim-text-curve],[data-dim-text-align]')
        || event.target;
      if (target?.dataset?.dimTextColour) {
        event.preventDefault();
        applyPatch(item.id, { colour: target.dataset.dimTextColour }, { reason: 'text-colour' });
      } else if (target?.dataset?.dimTextCurve !== undefined) {
        event.preventDefault();
        applyPatch(item.id, { curve: Number(target.dataset.dimTextCurve) }, { reason: 'text-curve' });
      } else if (target?.dataset?.dimTextAlign) {
        event.preventDefault();
        applyPatch(item.id, { align: target.dataset.dimTextAlign }, { reason: 'text-align' });
      }
    });

    populateFontSamples();
    populateFontChoices();
    refresh();

    const setPlacementGuidance = (status = null) => {
      const element = elements.placementGuidance;
      if (!element) return;
      const message = status === 'verified-rectangle'
        ? 'Keep your lettering within the safe area for this product.'
        : (status === 'advisory'
            ? 'Keep important lettering near the centre and check it carefully on the product preview.'
            : '');
      element.textContent = message;
      element.hidden = !message;
      if (message) element.setAttribute?.('role', 'note');
      else element.removeAttribute?.('role');
    };

    const setInkColours = (colours = null) => {
      if (colours !== null && (!Array.isArray(colours) || !colours.length || colours.length > 16
        || colours.some(colour => typeof colour !== 'string' || !/^#[0-9A-F]{6}$/.test(colour))
        || new Set(colours).size !== colours.length)) throw new Error('The admitted ink colours are unavailable.');
      inkColours = colours === null ? null : Object.freeze([...colours]);
      for (const [id, pending] of pendingInvalid) {
        if (pending.error?.code === 'WORDS_INK_COLOUR' && (inkColours === null || inkColours.includes(String(pending.value).toUpperCase()))) pendingInvalid.delete(id);
      }
      refresh();
    };

    return Object.freeze({
      getLayers: immutableLayers,
      getSelection: () => Object.freeze({ ...selection }),
      setSuggestion,
      selectImage,
      selectText,
      add,
      deleteSelected,
      duplicateSelected,
      setLayers,
      applyPatch,
      beginCoalescedEdit,
      endCoalescedEdit,
      undo,
      redo,
      hasPendingInvalid: () => pendingInvalid.size > 0,
      setPlacementGuidance,
      setInkColours,
      setLayoutWarning: (error = null) => {
        layoutWarning = error;
        refresh();
      },
      canExport: () => {
        if (destroyed || pendingInvalid.size || layoutWarning) return false;
        try { model.validateLayers(layers, { forExport: true }); return true; } catch { return false; }
      },
      assertPrintable: () => {
        const pending = firstPending();
        if (pending) throw pending.error;
        if (layoutWarning) throw layoutWarning;
        return Object.freeze(model.validateLayers(layers, { forExport: true }).map((layer) => Object.freeze(layer)));
      },
      destroy: () => {
        if (destroyed) return;
        destroyed = true;
        suggestion = null;
        if (suggestionState) {
          suggestionState.wording.textContent = '';
          suggestionState.container.hidden = true;
        }
        listeners.splice(0).forEach((remove) => remove());
      }
    });
  };

  return Object.freeze({ createTextControls });
}));
